import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { get, post } from '../lib/api';
import { Badge, Button, Card, ErrorNote, Loading } from '../ui';

/**
 * Every account the Google connection can actually see.
 *
 * Not `listAccessibleCustomers` — that returns only what the LOGIN sits on,
 * which for an agency is the manager account and nothing else. The clients
 * hang underneath and have to be asked for separately. Getting that wrong is
 * why the list looked empty while three real accounts were sitting there.
 */

type Row = {
  customer_id: string; name: string; currency: string; time_zone: string | null;
  manager: boolean; test_account: boolean; status: string | null; level: number;
  added_as: string | null;
};

const pretty = (id: string) =>
  id.length === 10 ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id;

export function GoogleAccounts({ connected }: { connected: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();

  const q = useQuery({
    queryKey: ['google-accounts'],
    queryFn: () => get<{ data: Row[] }>('/v1/google/accounts'),
    enabled: connected,
    staleTime: 300_000,
  });

  const add = useMutation({
    mutationFn: (customerId: string) => post<{ id: string }>(`/v1/google/accounts/${customerId}/add`),
    onSuccess: (r) => { qc.invalidateQueries(); nav(`/accounts/${r.id}`); },
  });

  if (!connected) {
    return (
      <Card className="mb-4">
        <h2 className="text-sm font-bold">Your Google Ads accounts</h2>
        <p className="mt-0.5 text-sm text-t2">
          Connect Google in Settings and every account your manager can reach appears here, ready to add in one click.
        </p>
      </Card>
    );
  }

  const rows = q.data?.data ?? [];
  const clients = rows.filter((r) => !r.manager);
  const managers = rows.filter((r) => r.manager);

  return (
    <Card className="mb-4" pad={false}>
      <div className="flex flex-wrap items-center justify-between gap-2 p-5 pb-3">
        <div>
          <h2 className="text-sm font-bold">Your Google Ads accounts</h2>
          <p className="text-xs text-t3">
            {managers.length
              ? `Everything under ${managers.map((m) => m.name).join(', ')}.`
              : 'Everything this connection can reach.'}
          </p>
        </div>
        <Button onClick={() => qc.invalidateQueries({ queryKey: ['google-accounts'] })}>
          <RefreshCw className={clsx('size-3.5', q.isFetching && 'animate-spin')} />Refresh
        </Button>
      </div>

      {q.isLoading ? <div className="px-5 pb-5"><Loading label="Asking Google" /></div>
        : q.error ? <div className="px-5 pb-5"><ErrorNote error={q.error} /></div>
        : !clients.length ? (
          <p className="px-5 pb-5 text-sm text-t3">
            No client accounts under that manager yet. Link one from the Google Ads interface —
            Accounts → + → Link existing account, read-only — and refresh.
          </p>
        ) : (
          <ul>
            {clients.map((a) => (
              <li key={a.customer_id} className="flex flex-wrap items-center gap-3 border-t border-bd px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{a.name}</span>
                    <span className="num text-xs text-t3">{pretty(a.customer_id)}</span>
                    {a.test_account && <Badge tone="low">test account</Badge>}
                    {a.status && a.status !== 'ENABLED' && <Badge tone="high">{a.status.toLowerCase()}</Badge>}
                  </div>
                  <div className="text-xs text-t3">{a.currency}{a.time_zone ? ` · ${a.time_zone}` : ''}</div>
                </div>
                {a.added_as ? (
                  <Button onClick={() => nav(`/accounts/${a.added_as}`)}>
                    <Check className="size-3.5" />Open
                  </Button>
                ) : (
                  <Button variant="primary" loading={add.isPending && add.variables === a.customer_id}
                    onClick={() => add.mutate(a.customer_id)}>
                    Add
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      {add.error != null && <div className="px-5 pb-5"><ErrorNote error={add.error} /></div>}
    </Card>
  );
}
